// src/pages/AuditTool.tsx
import React, { useState } from "react";
import {
  Card,
  Input,
  Button,
  Progress,
  Row,
  Col,
  Statistic,
  Alert,
  List,
  Typography,
  Tabs,
  Tag,
  Radio,
  Divider,
  theme,
} from "antd";
import {
  RocketOutlined,
  ThunderboltFilled,
  WarningOutlined,
  DashboardOutlined,
  CheckCircleOutlined,
  MobileOutlined,
  DesktopOutlined,
  BugOutlined,
  SafetyCertificateOutlined,
  ReadOutlined,
} from "@ant-design/icons";
import { runPageSpeedAudit } from "../services/api";

const { Title, Text } = Typography;

const AuditTool: React.FC = () => {
  const [url, setUrl] = useState("");
  const [strategy, setStrategy] = useState<"mobile" | "desktop">("mobile");
  const [report, setReport] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { token } = theme.useToken();

  const runAudit = async () => {
    if (!url) return;
    setLoading(true);
    setError("");
    setReport(null);
    try {
      const data = await runPageSpeedAudit(url, strategy);
      setReport(data);
    } catch (err: any) {
      setError(err.message || "Failed to analyze URL. Check API Key or Quota.");
    } finally {
      setLoading(false);
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 90) return "#4ade80"; // Green
    if (score >= 50) return "#facc15"; // Yellow
    return "#f87171"; // Red
  };

  // Helper to render the circular progress cards
  const ScoreCard = ({ title, categoryId, icon }: any) => {
    const cat = report?.lighthouseResult?.categories[categoryId];
    if (!cat) return null;
    const scoreVal = Math.round(cat.score * 100);

    return (
      <Card
        className="glass-card"
        bordered={false}
        bodyStyle={{ padding: "16px 8px", textAlign: "center" }} // Reduced padding for mobile
      >
        <div style={{ marginBottom: 12, fontSize: 20 }}>{icon}</div>
        <Progress
          type="circle"
          percent={scoreVal}
          width={60} // Slightly smaller circle for better mobile fit
          strokeColor={getScoreColor(scoreVal)}
          format={(p) => (
            <span
              style={{
                color: token.colorText,
                fontWeight: "bold",
                fontSize: "14px",
              }}
            >
              {p}
            </span>
          )}
        />
        <div
          style={{
            marginTop: 12,
            fontWeight: 600,
            color: token.colorText,
            fontSize: "13px",
          }}
        >
          {title}
        </div>
      </Card>
    );
  };

  // Helper to render lists of audits
  const AuditList = ({
    audits,
    type = "opportunity",
  }: {
    audits: any[];
    type?: string;
  }) => (
    <List
      dataSource={audits}
      renderItem={(item: any) => (
        <List.Item
          style={{
            padding: "12px 0",
            borderBottom: "1px solid rgba(255,255,255,0.05)",
            flexDirection: "column", // Stack items on very small screens if needed
            alignItems: "flex-start",
          }}
        >
          <div style={{ display: "flex", width: "100%", gap: 12 }}>
            <div style={{ marginTop: 4 }}>
              {type === "passed" ? (
                <CheckCircleOutlined
                  style={{ color: "#4ade80", fontSize: 18 }}
                />
              ) : (
                <WarningOutlined style={{ color: "#f87171", fontSize: 18 }} />
              )}
            </div>
            <div style={{ flex: 1 }}>
              <Text
                style={{
                  color: token.colorText,
                  fontSize: 15,
                  fontWeight: 500,
                  wordBreak: "break-word",
                }}
              >
                {item.title}
              </Text>
              {item.displayValue && (
                <div style={{ marginTop: 4 }}>
                  <Text type="secondary" style={{ fontSize: 13 }}>
                    {item.displayValue}
                  </Text>
                </div>
              )}
            </div>
          </div>

          {item.scoreDisplayMode === "numeric" && item.score !== 1 && (
            <div style={{ paddingLeft: 30, marginTop: 8 }}>
              <Tag color="red">Critical</Tag>
            </div>
          )}
        </List.Item>
      )}
    />
  );

  return (
    <div style={{ maxWidth: 1200, margin: "0 auto" }}>
      {/* Input Section */}
      <Card
        className="glass-card"
        bordered={false}
        style={{ marginBottom: 24 }}
        bodyStyle={{ padding: "16px" }}
      >
        <Row gutter={[12, 12]} align="middle">
          <Col xs={24} md={14} lg={16}>
            <Input
              size="large"
              placeholder="https://example.com"
              prefix={<DashboardOutlined style={{ color: "#666" }} />}
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              onPressEnter={runAudit}
              style={{ borderRadius: 8 }}
            />
          </Col>
          <Col xs={12} md={6} lg={4}>
            <Radio.Group
              value={strategy}
              onChange={(e) => setStrategy(e.target.value)}
              buttonStyle="solid"
              size="large"
              style={{ width: "100%", display: "flex" }}
            >
              <Radio.Button
                value="mobile"
                style={{ flex: 1, textAlign: "center", padding: 0 }}
              >
                <MobileOutlined />
              </Radio.Button>
              <Radio.Button
                value="desktop"
                style={{ flex: 1, textAlign: "center", padding: 0 }}
              >
                <DesktopOutlined />
              </Radio.Button>
            </Radio.Group>
          </Col>
          <Col xs={12} md={4} lg={4}>
            <Button
              type="primary"
              size="large"
              block
              icon={<RocketOutlined />}
              onClick={runAudit}
              loading={loading}
              style={{ borderRadius: 8 }}
            >
              Analyze
            </Button>
          </Col>
        </Row>
      </Card>

      {error && (
        <Alert
          message="Analysis Failed"
          description={error}
          type="error"
          showIcon
          style={{ marginBottom: 20, borderRadius: 8 }}
        />
      )}

      {report && (
        <div style={{ animation: "fadeIn 0.5s ease" }}>
          {/* Top Score Dashboard */}
          {/* Using grid of 2 columns on mobile (xs=12) instead of 1 stack */}
          <Row gutter={[12, 12]} style={{ marginBottom: 24 }}>
            <Col xs={12} sm={12} md={6}>
              <ScoreCard
                title="Performance"
                categoryId="performance"
                icon={<ThunderboltFilled style={{ color: "#facc15" }} />}
              />
            </Col>
            <Col xs={12} sm={12} md={6}>
              <ScoreCard
                title="Accessibility"
                categoryId="accessibility"
                icon={<ReadOutlined style={{ color: "#3b82f6" }} />}
              />
            </Col>
            <Col xs={12} sm={12} md={6}>
              <ScoreCard
                title="Best Practices"
                categoryId="best-practices"
                icon={
                  <SafetyCertificateOutlined style={{ color: "#4ade80" }} />
                }
              />
            </Col>
            <Col xs={12} sm={12} md={6}>
              <ScoreCard
                title="SEO"
                categoryId="seo"
                icon={<DashboardOutlined style={{ color: "#a855f7" }} />}
              />
            </Col>
          </Row>

          {/* Detailed Tabs */}
          <Card
            className="glass-card"
            bordered={false}
            bodyStyle={{ padding: "0 12px 12px 12px" }} // Tighter padding for mobile
          >
            <Tabs
              defaultActiveKey="1"
              size="large"
              tabBarStyle={{ marginBottom: 24 }}
              items={[
                {
                  key: "1",
                  label: (
                    <span style={{ fontSize: 14 }}>
                      <ThunderboltFilled /> Web Vitals
                    </span>
                  ),
                  children: (
                    <Row gutter={[24, 24]}>
                      <Col xs={24} lg={8}>
                        <Card
                          type="inner"
                          title="Core Metrics"
                          bordered={false}
                          style={{ background: "rgba(255,255,255,0.02)" }}
                          headStyle={{ color: token.colorText }}
                        >
                          <Statistic
                            title={
                              <span style={{ color: token.colorTextSecondary }}>
                                Largest Contentful Paint
                              </span>
                            }
                            value={
                              report.lighthouseResult.audits[
                                "largest-contentful-paint"
                              ].displayValue
                            }
                            valueStyle={{
                              color: token.colorText,
                              fontSize: 20,
                            }}
                            suffix={
                              <Tag
                                color={
                                  report.lighthouseResult.audits[
                                    "largest-contentful-paint"
                                  ].score > 0.9
                                    ? "green"
                                    : "red"
                                }
                              >
                                {Math.round(
                                  report.lighthouseResult.audits[
                                    "largest-contentful-paint"
                                  ].score * 100
                                )}
                              </Tag>
                            }
                          />
                          <Divider
                            style={{
                              margin: "12px 0",
                              opacity: 0.1,
                              borderColor: token.colorText,
                            }}
                          />
                          <Statistic
                            title={
                              <span style={{ color: token.colorTextSecondary }}>
                                Cumulative Layout Shift
                              </span>
                            }
                            value={
                              report.lighthouseResult.audits[
                                "cumulative-layout-shift"
                              ].displayValue
                            }
                            valueStyle={{
                              color: token.colorText,
                              fontSize: 20,
                            }}
                            suffix={
                              <Tag
                                color={
                                  report.lighthouseResult.audits[
                                    "cumulative-layout-shift"
                                  ].score > 0.9
                                    ? "green"
                                    : "red"
                                }
                              >
                                {Math.round(
                                  report.lighthouseResult.audits[
                                    "cumulative-layout-shift"
                                  ].score * 100
                                )}
                              </Tag>
                            }
                          />
                          <Divider
                            style={{
                              margin: "12px 0",
                              opacity: 0.1,
                              borderColor: token.colorText,
                            }}
                          />
                          <Statistic
                            title={
                              <span style={{ color: token.colorTextSecondary }}>
                                Total Blocking Time
                              </span>
                            }
                            value={
                              report.lighthouseResult.audits[
                                "total-blocking-time"
                              ].displayValue
                            }
                            valueStyle={{
                              color: token.colorText,
                              fontSize: 20,
                            }}
                          />
                        </Card>
                      </Col>
                      <Col xs={24} lg={16}>
                        <Title
                          level={5}
                          style={{ color: token.colorText, marginBottom: 16 }}
                        >
                          Top Optimization Opportunities
                        </Title>
                        <AuditList
                          audits={Object.values(
                            report.lighthouseResult.audits
                          ).filter(
                            (a: any) =>
                              a.score !== null &&
                              a.score < 0.9 &&
                              a.details?.type === "opportunity"
                          )}
                        />
                      </Col>
                    </Row>
                  ),
                },
                {
                  key: "2",
                  label: (
                    <span style={{ fontSize: 14 }}>
                      <BugOutlined /> Diagnostics
                    </span>
                  ),
                  children: (
                    <Row gutter={[24, 24]}>
                      <Col span={24}>
                        <Alert
                          message="Analyzing URL"
                          description={
                            <span style={{ wordBreak: "break-all" }}>
                              {report.lighthouseResult.finalUrl}
                            </span>
                          }
                          type="info"
                          showIcon
                          style={{
                            marginBottom: 20,
                            background: "rgba(24, 144, 255, 0.1)",
                            border: "1px solid #1890ff",
                          }}
                        />
                        <Title level={5} style={{ color: token.colorText }}>
                          Failed Audits & Warnings
                        </Title>
                        <AuditList
                          audits={Object.values(report.lighthouseResult.audits)
                            .filter(
                              (a: any) =>
                                a.score !== null &&
                                a.score < 0.5 &&
                                !a.details?.type
                            )
                            .slice(0, 10)}
                        />
                      </Col>
                    </Row>
                  ),
                },
                {
                  key: "3",
                  label: (
                    <span style={{ fontSize: 14 }}>
                      <CheckCircleOutlined /> Passed
                    </span>
                  ),
                  children: (
                    <AuditList
                      type="passed"
                      audits={Object.values(report.lighthouseResult.audits)
                        .filter(
                          (a: any) =>
                            a.score === 1 ||
                            (a.score === null && a.displayValue === "")
                        )
                        .slice(0, 10)}
                    />
                  ),
                },
              ]}
            />
          </Card>
        </div>
      )}
    </div>
  );
};

export default AuditTool;
